// routes/ThreadPane.jsx — 대화 (v5 §12 시안 2a — 말풍선 채팅).
//
// 규칙은 개편 전과 같다:
//   D-2  전송 경로는 <Form> 하나. 단축키는 requestSubmit(). 낙관적 추가 없음.
//   D-8  기록을 합치지 않는다 — 같은 작성자의 연속 발화도 말풍선이 따로 선다.
//   B-8  AI 라벨은 **모든** AI 발화에 붙는다. 그래서 AI 발화는 그룹으로 묶지 않는다.
//   IME  조합 중 Enter 는 전송되지 않는다.
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'wouter-preact';
import { command, read } from '../api/client.js';
import { readForwardPages } from '../api/read-pages.js';
import { WriteBoundary } from '../api/write-boundary.js';
import { useCommand, useResource } from '../api/resource.js';
import { describeError } from '../api/errors.js';
import { useSession } from '../session/session.jsx';
import { Avatar, Button, ButtonLink, Dialog, Field, Form, Icon, Input, Notice, Textarea } from '../ui/primitives.jsx';
import { Empty, Resource } from '../ui/states.jsx';
import { isEligibleTaskRecipient } from './task-members.js';
import { isAiMessage, messageAuthorName } from './message-presentation.js';
import { useNativeConversationCapabilities } from './conversation-capabilities.js';
import './thread.css';
import { ConversationActivity } from './ConversationActivity.jsx';

export function ThreadPane({ spaceId, threadId, archived, members = [] }) {
  const session = useSession();
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  // Task 원문 범위는 `마지막으로 선택한 Message #N` 이다. Thread 전체를 암묵적으로
  // 붙이지 않는다 — #1부터 이 번호까지가 Message Server가 확인할 snapshot이다.
  const [throughSequence, setThroughSequence] = useState(null);
  const ledgerRef = useRef(null);
  const scope = `${session.nodeId}:${spaceId}:${threadId}`;
  const pageRef = useRef({ scope, trail: [0], observedTail: null });
  if (pageRef.current.scope !== scope) pageRef.current = { scope, trail: [0], observedTail: null };
  const page = pageRef.current;
  // 새 기록을 남긴 사람이 하단을 보고 있을 때만 다음 서버 읽기 뒤 맨 아래를
  // 보여 준다. 과거 기록을 읽고 있던 사람의 위치를 빼앗지 않는다.
  const stickToLatestRef = useRef(true);

  // 다른 Thread로 이동한 뒤 이전 대화의 #N을 그대로 seed로 쓰지 않는다.
  // 선택은 이 Thread의 원문 범위이므로, 대화가 바뀌면 다시 고른다.
  useLayoutEffect(() => {
    setTaskFormOpen(false);
    setThroughSequence(null);
    stickToLatestRef.current = true;
  }, [scope]);

  const conversation = useResource(`space:${spaceId}:thread:${threadId}`, (signal) =>
    readForwardPages((afterSequence, limit) => read('conversation-read', {
      nodeId: session.nodeId, query: { spaceId, threadId, afterSequence, limit }, signal,
    }), { field: 'messages', id: 'messageId', cursorField: 'nextAfterSequence',
      start: page.trail.at(-1), maxPages: 1, signal }),
    { quiet: true, identity: session.nodeId },
  );
  const threads = useResource(`space:${spaceId}:threads`, () =>
    read('conversation-list', { nodeId: session.nodeId, query: { spaceId } }),
  );
  const provider = useNativeConversationCapabilities(spaceId);

  const thread =
    threads.status === 'ready'
      ? (threads.data.threads ?? []).find((t) => t.threadId === threadId)
      : null;

  const closed = thread?.state === 'closed';
  const taskWritable = !archived && !closed && conversation.status === 'ready';
  // Task 생성은 Ledger의 별도 쓰기지만, 원문 Thread의 새 기록과 새 Thread 생성은
  // provider의 append capability 없이는 UI에서 시도하지 않는다.
  const conversationWritable = taskWritable && provider.canAppend;
  const messages = conversation.status === 'ready' ? (conversation.data.messages ?? []) : [];
  const latest = conversation.status === 'ready' ? validatedLatestSequence(conversation.data) : null;
  if (latest !== null && page.observedTail === null) page.observedTail = latest;
  const newTail = latest !== null && latest > page.observedTail;
  const openWindow = (after, { scroll = false, observe = false } = {}) => {
    if (page.trail.at(-1) !== after) page.trail = [...page.trail, after];
    if (observe && latest !== null) page.observedTail = latest;
    page.ownSequence = null;
    stickToLatestRef.current = scroll;
    conversation.reload();
  };

  useLayoutEffect(() => {
    if (!stickToLatestRef.current || !ledgerRef.current) return;
    ledgerRef.current.scrollTop = ledgerRef.current.scrollHeight;
    stickToLatestRef.current = false;
  }, [scope, conversation.data]);

  // Older append responses may omit the persisted sequence. One fresh snapshot
  // can locate its tail; never scan history or infer a tail from a full page.
  useLayoutEffect(() => {
    const pending = page.pendingOwn;
    if (!pending || conversation.isStale || conversation.status !== 'ready'
      || conversation.data === pending.before) return;
    page.pendingOwn = null;
    if (latest === null) return;
    if (pending.follow) openWindow(Math.max(0, latest - 100), { scroll: true });
  }, [conversation.data, conversation.isStale, scope]);

  const refreshConversationAfterAppend = (result) => {
    // A completed command for a previous Node/Thread must not navigate this one.
    if (pageRef.current !== page) return;
    const ledger = ledgerRef.current;
    // 64px은 작성자가 마지막 말풍선을 보고 있는 자연스러운 오차 범위다.
    // 이 밖의 위치라면 읽는 맥락을 존중한다.
    const follow = !ledger || ledger.scrollHeight - ledger.scrollTop - ledger.clientHeight < 64;
    const sequence = result?.message?.sequence;
    if (Number.isSafeInteger(sequence) && sequence > 0) {
      if (follow) {
        openWindow(Math.max(0, sequence - 100), { scroll: true });
        return;
      }
      page.ownSequence = sequence;
    } else {
      page.pendingOwn = { before: conversation.data, follow };
    }
    // invalidate()는 캐시를 비워 목록을 잠시 사라지게 한다. message append 뒤에는
    // 기존 원문을 유지한 채 reload()로 재검증해야 채팅이 위로 튀지 않는다.
    conversation.reload();
  };

  return (
    <WriteBoundary resources={[conversation]}><section class="work work--chat">
      <header class="work__head work__head--chat">
        <div class="chat-bar__identity">
          <ButtonLink class="chat-bar__back" size="xs" tone="ghost" href={`/s/${spaceId}/threads`}>
            <Icon name="arrow" rotate={180} /> 대화
          </ButtonLink>
          <h1 class="chat-bar__title">{thread?.topic ?? '대화'}</h1>
        </div>
        <div class="chat-bar__actions">
          {taskWritable ? (
            <Button size="xs" onClick={() => setTaskFormOpen(true)}>
              Task 만들기
            </Button>
          ) : null}
        </div>
      </header>

      <div class="work__body work__body--fill">
        <div class="chat-layout">
          <div class="chat-main">
            <div class="ledger" ref={ledgerRef}>
              <Resource resource={conversation} loadingLabel="기록을 읽는 중">
                {(data) => <>
                  {newTail || (latest !== null && (messages.at(-1)?.sequence ?? 0) < latest) || page.ownSequence ? <div class="ledger__navigation" role="group" aria-label="대화 기록 이동">
                    {newTail ? <p class="t-meta">새 기록이 있습니다.</p> : null}
                    {newTail ? <Button size="xs" disabled={conversation.isStale} onClick={() => openWindow(page.observedTail, { observe: true })}>새 기록부터 보기</Button> : null}
                    {latest !== null && (messages.at(-1)?.sequence ?? 0) < latest ? <Button size="xs" disabled={conversation.isStale} onClick={() => openWindow(Math.max(0, latest - 100), { observe: true })}>최근 기록 보기</Button> : null}
                    {page.ownSequence ? <Button size="xs" disabled={conversation.isStale} onClick={() => openWindow(Math.max(0, page.ownSequence - 100), { scroll: true })}>내 기록 보기</Button> : null}
                  </div> : null}
                  {(data.messages ?? []).length === 0 ? (
                    <Empty title={pageRef.current.trail.at(-1) ? '이 구간에는 더 새 기록이 없습니다.' : '아직 기록이 없습니다.'}>
                      {pageRef.current.trail.at(-1) ? '이전 구간에서 앞선 기록을 확인할 수 있습니다.' : '첫 기록을 남기면 이 자리에 순서대로 쌓입니다.'}
                    </Empty>
                  ) : (
                    <ol class="ledger__list">
                      {withDividers(data.messages).map((entry) =>
                        entry.divider ? (
                          <li class="day-divider t-meta" key={`day-${entry.day}`}>{entry.day}</li>
                        ) : (
                          <MessageRow
                            key={entry.message.messageId}
                            message={entry.message}
                            repeatsAuthor={entry.repeatsAuthor}
                            isMine={entry.message.authorNodeId === session.nodeId}
                            members={members}
                            choosingRange={false}
                            throughSequence={throughSequence}
                            onChooseThroughSequence={setThroughSequence}
                          />
                        ),
                      )}
                    </ol>
                  )}
                  {data.incomplete || pageRef.current.trail.length > 1 ? <div class="row">
                    {data.incomplete ? <p class="t-meta">기록의 일부를 보고 있습니다. 다음 구간에서 이어서 확인할 수 있습니다.</p> : null}
                    {pageRef.current.trail.length > 1 ? <Button size="xs" disabled={conversation.isStale} onClick={() => {
                      pageRef.current.trail = pageRef.current.trail.slice(0, -1); conversation.reload();
                    }}>이전 기록 보기</Button> : null}
                    {data.hasMore ? <Button size="xs" disabled={conversation.isStale} onClick={() => {
                      pageRef.current.trail = [...pageRef.current.trail, data.nextAfterSequence]; conversation.reload();
                    }}>다음 기록 보기</Button> : null}
                  </div> : null}
                </>}
              </Resource>
              {conversation.status === 'ready' ? <ConversationActivity key={`${spaceId}:${threadId}`}
                spaceId={spaceId} threadId={threadId} members={members} archived={archived} /> : null}
            </div>

            {taskWritable ? (
              // Task 작성은 대화 화면의 다음 단계이므로 모달로 열고, 기록·작성창은
              // 캔버스 자리에 그대로 남긴다.
              taskFormOpen ? (
                <TaskCreate
                  key={scope}
                  spaceId={spaceId}
                  threadId={threadId}
                  messages={messages}
                  throughSequence={throughSequence}
                  onChooseThroughSequence={setThroughSequence}
                  members={members}
                  close={() => setTaskFormOpen(false)}
                />
              ) : conversationWritable ? (
                <Composer
                  key={scope}
                  spaceId={spaceId}
                  threadId={threadId}
                  onSubmitted={refreshConversationAfterAppend}
                />
              ) : provider.resource.status === 'ready' ? (
                <div class="composer composer--closed">
                  <p class="t-meta">이 원문 연결은 새 기록을 받지 않습니다. Task 작업은 계속할 수 있습니다.</p>
                </div>
              ) : provider.resource.status === 'error' ? (
                <Notice tone="danger" title="원문 대화의 쓰기 권한을 확인하지 못했습니다.">
                  다시 읽은 뒤에만 새 기록을 남길 수 있습니다. 기록과 Task는 각자의 읽기 결과가 있으면 계속 볼 수 있습니다.
                </Notice>
              ) : (
                <div class="composer composer--closed"><p class="t-meta">원문 기록 권한을 확인하는 중입니다.</p></div>
              )
            ) : (
              <div class="composer composer--closed">
                <p class="t-meta">
                  {conversation.status !== 'ready'
                    ? '대화 상태를 확인한 뒤 기록을 남길 수 있습니다.'
                    : archived ? '보관된 공간입니다. 새 기록을 남길 수 없습니다.'
                    : '닫힌 대화입니다. 새 기록을 남길 수 없습니다.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section></WriteBoundary>
  );
}

// This optional snapshot watermark is advisory. Malformed/contradictory values
// fall back to explicit ascending pagination, never a claim of being current.
function validatedLatestSequence(data) {
  const latest = data.latestSequence;
  if (!Number.isSafeInteger(latest) || latest < 0) return null;
  if ((data.messages ?? []).some((message) => !Number.isSafeInteger(message.sequence)
    || message.sequence < 1 || message.sequence > latest)) return null;
  if (data.nextAfterSequence != null && (!Number.isSafeInteger(data.nextAfterSequence)
    || data.nextAfterSequence < 0
    || ((data.messages ?? []).length > 0 && data.nextAfterSequence > latest))) return null;
  return latest;
}

/** 날짜 구분과 「같은 작성자 연속」 표시를 한 번에 계산한다. AI 는 연속으로 치지 않는다. */
function withDividers(messages) {
  const out = [];
  let lastDay = null;
  let lastAuthor = null;
  for (const message of messages) {
    const day = dayOf(message.createdAt);
    if (day && day !== lastDay) {
      out.push({ divider: true, day });
      lastDay = day;
      lastAuthor = null;
    }
    const ai = isAiMessage(message);
    out.push({ message, repeatsAuthor: !ai && lastAuthor === message.authorNodeId });
    lastAuthor = ai ? null : message.authorNodeId;
  }
  return out;
}

function MessageRow({ message, repeatsAuthor, isMine, members, choosingRange, throughSequence, onChooseThroughSequence }) {
  const ai = isAiMessage(message);
  const authorName = messageAuthorName({ authorNodeId: message.authorNodeId, isMine, members });

  return (
    <li class={`msg${isMine ? ' msg--mine' : ''}${ai ? ' msg--ai' : ''}${choosingRange && message.sequence <= throughSequence ? ' msg--in-task-range' : ''}`}>
      {/* 시안 2a: 내 발화에는 아바타가 없다 — 이름·시각 메타만 우측 정렬로 선다.
          이름과 이니셜이 함께 붙으면 「나 11:25 나」로 읽힌다(사람 스크린샷 지적). */}
      {isMine ? null : repeatsAuthor ? <span class="avatar" style="visibility: hidden" aria-hidden="true" /> : <Avatar label={authorName} ai={ai} />}
      <div class="msg__col">
        {repeatsAuthor && !ai ? null : (
          <div class="msg__meta t-meta">
            {isMine ? (
              <>
                <time class="t-mono" dateTime={message.createdAt}>{formatTime(message.createdAt)}</time>
                <span class="msg__author t-meta">{authorName}</span>
              </>
            ) : (
              <>
                <span class="msg__author t-meta">{authorName}</span>
                {ai ? <span class="msg__tag">AI 발화</span> : null}
                <time class="t-mono" dateTime={message.createdAt}>{formatTime(message.createdAt)}</time>
              </>
            )}
          </div>
        )}

        {message.retracted ? (
          <p class="bubble bubble--retracted t-body">작성자가 철회한 기록입니다.</p>
        ) : (
          <p class={`bubble t-body${repeatsAuthor ? '' : ' bubble--first'}`}>{message.body}</p>
        )}

        {message.revisionNumber > 1 && !message.retracted ? (
          <p class="t-meta msg__revised">고쳐 씀 · {message.revisionNumber}판</p>
        ) : null}

        {choosingRange ? (
          <Button
            size="sm"
            tone={throughSequence === message.sequence ? 'neutral' : 'ghost'}
            aria-pressed={throughSequence === message.sequence}
            class="msg__range-select"
            onClick={() => onChooseThroughSequence(message.sequence)}
          >
            {throughSequence === message.sequence ? `원문 범위 끝 #${message.sequence}` : `여기까지 선택 · #${message.sequence}`}
          </Button>
        ) : null}

        {(message.references ?? []).length > 0 ? (
          <div class="msg__refs">
            {message.references.map((r) => (
              <span key={r.referenceId} class="msg__ref t-meta t-mono">
                <Icon name="resource" width="14" height="14" />
                {r.referenceId}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Task는 독립된 대화 복사본이 아니라 원문 Thread locator를 참조한다. 생성 때부터
 * origin을 붙여야 Task 상세에서 다시 이 대화로 돌아갈 수 있다.
 */
function TaskCreate({ spaceId, threadId, messages, throughSequence, onChooseThroughSequence, members, close }) {
  const session = useSession();
  const [, navigate] = useLocation();
  const titleRef = useRef(null);
  const purposeRef = useRef(null);
  const externalUrlRef = useRef(null);
  const create = useCommand(
    (params, idempotencyKey) => command('task_create', params, { nodeId: session.nodeId, idempotencyKey }),
    { invalidates: [`space:${spaceId}:tasks`] },
  );
  // Space 멤버십 응답의 state 부재까지 한곳에서 처리한다.
  const eligible = members.filter(isEligibleTaskRecipient);
  const otherEligible = eligible.filter((member) => member.nodeId !== session.nodeId);

  return (
    <Dialog title="이 대화에서 Task 만들기" onClose={close}>
      <Form
        class="stack task-create-form"
        busy={create.pending}
        onSubmit={async (event) => {
        // Form-local refs intentionally avoid a parent re-render on every IME
        // composition update. Korean composition must keep the DOM focus.
        const title = titleRef.current?.value?.trim() ?? '';
        const purpose = purposeRef.current?.value?.trim() ?? '';
        const externalUrl = externalUrlRef.current?.value?.trim() ?? '';
        if (!title || !purpose || !throughSequence) return;
        const participantNodeIds = new FormData(event.currentTarget).getAll('participants');
        const initialReferences = [{
          kind: 'thread', relation: 'origin', mode: 'snapshot', locator: { threadId, throughSequence },
        }];
        if (externalUrl) {
          initialReferences.push({
            kind: 'url', relation: 'evidence', mode: 'live', locator: { url: externalUrl },
          });
        }
        const result = await create.run({
          spaceId,
          title,
          purpose,
          participantNodeIds,
          initialReferences,
        });
        const taskId = result.data?.taskId;
        if (result.ok && taskId) navigate(`/s/${spaceId}/task/${taskId}`);
        }}
      >
        <Field label="Task 제목" htmlFor="taskTitle" required>
          <Input ref={titleRef} id="taskTitle" name="title" />
        </Field>
        <Field label="함께 풀 판단" htmlFor="taskPurpose" required>
          <Textarea ref={purposeRef} id="taskPurpose" name="purpose" />
        </Field>
        <fieldset class={`task-seed-range${throughSequence ? ' is-selected' : ''}`}>
          <legend class="t-body-strong">원문 범위</legend>
          <p class="t-meta">Task의 근거로 남길 마지막 기록을 고르세요. 원문 내용은 복사하지 않습니다.</p>
          <div class="task-seed-picker" role="radiogroup" aria-label="Task 원문 범위">
            {messages.map((message) => (
              <label class={`task-seed-option${throughSequence === message.sequence ? ' is-selected' : ''}`} key={message.messageId}>
                <input
                  type="radio"
                  name="throughSequence"
                  value={message.sequence}
                  checked={throughSequence === message.sequence}
                  onChange={() => onChooseThroughSequence(message.sequence)}
                />
                <span>기록 #{message.sequence}</span>
                <time class="t-mono" dateTime={message.createdAt}>{formatTime(message.createdAt)}</time>
              </label>
            ))}
          </div>
          {throughSequence ? (
            <p class="t-meta">첫 기록부터 #{throughSequence}까지 {messages.filter((message) => message.sequence <= throughSequence).length}개 locator를 snapshot으로 연결합니다.</p>
          ) : null}
        </fieldset>
        <Field label="외부 원문 URL (선택)" htmlFor="taskExternalUrl">
          <Input ref={externalUrlRef} id="taskExternalUrl" name="externalUrl" type="url" placeholder="원문 URL을 입력하세요" />
        </Field>
        <p class="t-meta">URL은 원문 위치만 연결합니다. 파일·본문·토큰은 Task에 복사하지 않습니다.</p>
        {otherEligible.length > 0 ? (
          <fieldset class="stack">
            <legend class="t-body-strong">함께 참여할 사람 (선택)</legend>
            {otherEligible.map((member) => (
              <label class="row" key={member.nodeId}>
                <input type="checkbox" name="participants" value={member.nodeId} />
                <span>{member.displayName ?? '이름 없는 자리'}</span>
              </label>
            ))}
          </fieldset>
        ) : <p class="t-meta">현재 함께 참여시킬 수 있는 활성 구성원이 없습니다.</p>}
        {create.error ? <Notice tone="danger" title={describeError(create.error).message} /> : null}
        <div class="row row--between">
          <Button size="sm" tone="ghost" onClick={close}>취소</Button>
          <Button kind="submit" size="sm" busy={create.pending} disabled={!throughSequence}>Task 만들기</Button>
        </div>
      </Form>
    </Dialog>
  );
}

function Composer({ spaceId, threadId, onSubmitted }) {
  const session = useSession();
  const bodyRef = useRef(null);
  // IME 조합 중에는 단축키를 가로채지 않는다. 한글 입력에서 조합 확정 Enter 가
  // 전송으로 새어 나가는 일을 막는다.
  const composingRef = useRef(false);

  // textarea의 높이는 사용자가 모서리를 끌어 조절하는 값이 아니라, 실제 입력 줄 수를
  // 따른다. max-height를 넘으면 그때만 입력창 내부에서 스크롤한다.
  const fitToContent = (element) => {
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  };

  const append = useCommand(
    (params, idempotencyKey) => command('message_append', params, { nodeId: session.nodeId, idempotencyKey }),
    // 전송 성공은 `onSubmitted`에서 기존 원문을 지키며 다시 읽는다. 여기서
    // invalidate를 쓰면 cache가 비어 기록 전체가 loading으로 교체된다.
  );

  const submit = async () => {
    const body = bodyRef.current?.value?.trim() ?? '';
    if (body === '') return;
    const result = await append.run({ spaceId, threadId, body });
    if (result.ok) {
      if (bodyRef.current) {
        bodyRef.current.value = '';
        bodyRef.current.style.height = '';
      }
      onSubmitted?.(result.data);
    }
  };

  return (
    <Form class="composer" busy={append.pending} onSubmit={submit}>
      <label class="visually-hidden" for="composerBody">새 기록</label>
      <Textarea
        id="composerBody"
        name="body"
        rows={1}
        ref={bodyRef}
        placeholder="이 대화에 남길 기록을 작성하세요"
        onInput={(event) => fitToContent(event.currentTarget)}
        onCompositionStart={() => { composingRef.current = true; }}
        onCompositionEnd={() => { composingRef.current = false; }}
        onKeyDown={(e) => {
          if (composingRef.current) return;
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            // 단축키도 **같은 제출 경로**를 탄다. 이것이 D-2 가 재발할 수 없는 이유다.
            e.currentTarget.form?.requestSubmit();
          }
        }}
      />
      <div class="composer__foot">
        {/* 첨부·이미지·다른 작성 도구는 실제 provider capability가 생길 때 이 slot에만
            추가한다. 현재 텍스트 전송 경로에 없는 행동을 버튼으로 흉내 내지 않는다. */}
        <div class="composer__addons" aria-label="대화 확장 도구" />
        <p class="t-meta composer__hint">Enter는 줄바꿈입니다. 보내기는 버튼 또는 ⌘/Ctrl + Enter로만 실행됩니다.</p>
        <Button kind="submit" size="sm" busy={append.pending}>보내기</Button>
      </div>
      {append.error ? (
        <Notice tone="danger" title={describeError(append.error).message} />
      ) : null}
    </Form>
  );
}

export function NewThreadPane({ spaceId, archived, first = false }) {
  const session = useSession();
  const [, navigate] = useLocation();
  const topicRef = useRef(null);
  const provider = useNativeConversationCapabilities(spaceId);

  const create = useCommand(
    (params, idempotencyKey) => command('thread_create', params, { nodeId: session.nodeId, idempotencyKey }),
    { invalidates: [`space:${spaceId}:threads`] },
  );
  const close = () => navigate(`/s/${spaceId}/threads`);

  if (archived) {
    return (
      <Dialog title="새 대화" onClose={close}>
          <Notice tone="neutral" title="보관된 공간입니다.">
            기록은 읽을 수 있고 새 대화는 열 수 없습니다.
          </Notice>
      </Dialog>
    );
  }

  if (provider.resource.status === 'ready' && !provider.canAppend) {
    return (
      <Dialog title="새 대화" onClose={close}>
          <Notice tone="neutral" title="이 원문 연결에서는 새 대화를 열 수 없습니다.">
            현재 Provider가 쓰기 기능을 제공하지 않습니다. 이미 열린 원문과 Task 기록은 계속 읽을 수 있습니다.
          </Notice>
      </Dialog>
    );
  }

  if (provider.resource.status === 'error') {
    return (
      <Dialog title="새 대화" onClose={close}>
          <Notice tone="danger" title="새 대화를 열 권한을 확인하지 못했습니다.">
            연결 상태를 다시 읽은 뒤에만 새 대화를 시작할 수 있습니다.
          </Notice>
      </Dialog>
    );
  }

  return (
    <Dialog title={first ? '첫 대화 시작' : '새 대화'} onClose={close}>
      <Form
        class="stack new-thread-form"
        busy={create.pending || provider.resource.status !== 'ready'}
        onSubmit={async () => {
          const topic = topicRef.current?.value?.trim() ?? '';
          if (topic === '') return;
          const result = await create.run({ spaceId, topic });
          const threadId = result.data?.thread?.threadId ?? result.data?.threadId;
          // Creating a Thread is a forward navigation. Do not replace the
          // form route: browser back must return people to their prior page.
          if (result.ok && threadId) navigate(`/s/${spaceId}/t/${threadId}`);
        }}
      >
        <Field label="대화 제목" htmlFor="threadTopic" required>
          <Input
            id="threadTopic"
            name="topic"
            ref={topicRef}
            placeholder="예: 이번 주에 같이 결정할 일"
            autoFocus
          />
        </Field>

        <p class="t-meta new-thread-form__note">대화 제목만 먼저 정합니다. Task는 필요할 때 만듭니다.</p>

        {provider.resource.status === 'loading' ? (
          <p class="t-meta">원문 대화 연결을 확인하는 중입니다.</p>
        ) : null}

        {create.error ? (
          <Notice tone="danger" title={describeError(create.error).message} />
        ) : null}

        <div class="row row--between">
          <Button size="sm" tone="ghost" onClick={close}>취소</Button>
          <Button kind="submit" size="sm" busy={create.pending} disabled={provider.resource.status !== 'ready'}>
            {create.pending ? '여는 중' : '대화 열기'}
          </Button>
        </div>
      </Form>
    </Dialog>
  );
}

function dayOf(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('ko-KR', { month: 'long', day: 'numeric', weekday: 'long' }).format(d);
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('ko-KR', { hour: '2-digit', minute: '2-digit' }).format(d);
}
